/**
 * Soft clarifying helpers for incomplete / non-English asks.
 * Keep wording simple — many Studio users are non-tech.
 */

export type SlateBareQueryKind = 'phone' | 'email' | 'title';

export interface SlateBareQuery {
    kind: SlateBareQueryKind;
    value: string;
}

export interface SoftClarifyPack {
    title: string;
    body: string;
    chips: string[];
}

/** Studio action words — if present, not a "bare" name/number dump. */
const COMMAND_HINT =
    /\b(open|show|list|how|where|who|what|find|search|tell|views?|viewing|analytics|metric|status|purchase|sales?|upload|help|guide|wallet|report|dashboard|top|last|this|previous|yesterday|today|please|filmmaker|film\s*maker|viewer|admin|curator|partner|revenue|earnings|minutes?|watched|watch\s*time|plays?|tickets?|balance|level|contest|settings?|approve|reject|draft|navigate|go\s+to|take\s+me)\b/i;

/**
 * True when message is mostly another script / language
 * (Hindi, Gujarati, Tamil, Arabic, Chinese, etc.).
 */
export function detectNonEnglishAsk(text: string): boolean {
    const t = text.trim();
    if (!t) return false;

    const hasOtherScript =
        /[\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0600-\u06FF\u0750-\u077F\u0400-\u04FF\u4E00-\u9FFF\u3040-\u30FF\uAC00-\uD7AF]/.test(
            t
        );
    if (!hasOtherScript) return false;

    const letters = (t.match(/[^\s\d.,!?;:'"()\-+/=@#]/g) || []).length;
    const other = (
        t.match(
            /[\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0600-\u06FF\u0400-\u04FF\u4E00-\u9FFF\u3040-\u30FF\uAC00-\uD7AF]/g
        ) || []
    ).length;
    return other / Math.max(letters, 1) >= 0.25;
}

/** Friendly English-only redirect — no blame, easy examples. */
export function nonEnglishClarifyPack(
    audience: 'admin' | 'filmmaker'
): SoftClarifyPack {
    const chips =
        audience === 'filmmaker'
            ? [
                  'Show my films',
                  'How do I upload a film?',
                  'My film views last 7 days',
                  'Where is my wallet?',
              ]
            : [
                  'Open film "Movie Name" in view mode',
                  'What is the status of "Movie Name"?',
                  'Who is mobile 9876543210?',
                  'Film views last 7 days',
                  'How do I upload a film?',
              ];

    return {
        title: 'Please ask in English',
        body:
            'No problem — Talkie understands English best right now. ' +
            'Please type your question again in simple English. ' +
            'You can tap an example below, or write like: “Open film Inception” or “Who is mobile 9876543210?”. ' +
            'Thank you for understanding.',
        chips,
    };
}

/**
 * Bare phone / email / film-or-person name with no clear action.
 * Returns null when text already looks like a full Studio question.
 */
export function detectBareQuery(text: string): SlateBareQuery | null {
    const raw = text.trim().replace(/\s+/g, ' ');
    if (!raw || raw.length > 90) return null;
    if (/^(hi|hello|hey|help|thanks|thank you|ok|okay|yes|no)[.!]*$/i.test(raw)) {
        return null;
    }

    const digits = raw.replace(/[\s\-().+]/g, '');
    if (
        /^(\+?91)?[6-9]\d{9}$/.test(digits) ||
        (/^\d{10}$/.test(digits) && digits.length === 10)
    ) {
        const phone = digits.replace(/^\+?91/, '').slice(-10);
        return { kind: 'phone', value: phone };
    }

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(raw)) {
        return { kind: 'email', value: raw.toLowerCase() };
    }

    const quotedOnly = raw.match(/^["']([^"']{2,80})["']$/);
    if (quotedOnly?.[1]) {
        return { kind: 'title', value: quotedOnly[1].trim() };
    }

    // Has Studio action words → not bare
    if (COMMAND_HINT.test(raw)) return null;

    const words = raw.split(/\s+/);
    if (words.length < 1 || words.length > 6) return null;

    // Pure short number that is not a phone
    if (/^\d{1,9}$/.test(raw)) return null;

    // Title / name shaped (Latin letters, digits, light punctuation)
    if (!/^[A-Za-z0-9][A-Za-z0-9\s.''&\-:]*$/.test(raw)) return null;

    return { kind: 'title', value: raw.replace(/^["']|["']$/g, '').trim() };
}

export function bareQueryClarifyPack(
    bare: SlateBareQuery,
    audience: 'admin' | 'filmmaker'
): SoftClarifyPack {
    if (bare.kind === 'phone' || bare.kind === 'email') {
        return contactClarifyPack(bare, audience);
    }
    return titleClarifyPack(bare.value, audience);
}

function contactClarifyPack(
    bare: SlateBareQuery,
    audience: 'admin' | 'filmmaker'
): SoftClarifyPack {
    const label =
        bare.kind === 'phone' ? `number ${bare.value}` : `email ${bare.value}`;

    if (audience === 'filmmaker') {
        return {
            title: 'What would you like to do?',
            body:
                `I see a ${bare.kind === 'phone' ? 'phone number' : 'email'}. ` +
                'Filmmaker Studio cannot search other users. ' +
                'Please ask about your own films in simple English, or tap an example below.',
            chips: [
                'Show my films',
                'How do I upload a film?',
                'My film views last 7 days',
                'Where is my wallet?',
            ],
        };
    }

    const phoneOrEmail =
        bare.kind === 'phone'
            ? `mobile ${bare.value}`
            : bare.value;

    return {
        title: 'What should I do with this?',
        body:
            `I see the ${label}. Please choose what you want, or type in simple English.`,
        chips: [
            `Who is ${phoneOrEmail}?`,
            bare.kind === 'phone'
                ? `Find viewer with phone ${bare.value}`
                : `Find viewer with email ${bare.value}`,
            bare.kind === 'phone'
                ? `Find filmmaker with phone ${bare.value}`
                : `Find filmmaker with email ${bare.value}`,
            bare.kind === 'phone'
                ? `Open filmmaker with mobile ${bare.value}`
                : `Open filmmaker "${bare.value}"`,
        ],
    };
}

function titleClarifyPack(
    title: string,
    audience: 'admin' | 'filmmaker'
): SoftClarifyPack {
    const q = title.includes('"') ? title : `"${title}"`;
    const chips = [
        `Open film ${q} in view mode`,
        `What is the status of ${q}?`,
        `Who is the filmmaker of ${q}?`,
        `Tell me about ${q}`,
        `Views for ${q} last 7 days`,
    ];
    if (audience === 'admin') {
        chips.splice(4, 0, `How many purchases for ${q}?`);
    }

    return {
        title: 'What do you want to know?',
        body:
            `I see “${title}”. Please tell me what you need — open the film, check status, find the filmmaker, or something else. ` +
            'Tap a button below, or type a short English sentence.',
        chips: chips.slice(0, 5),
    };
}
